import React, { useState } from 'react';
import cx from 'classnames';

import { hasAcceptedFileType } from '@trqk/utils';

import './file-drop-zone.css';

interface Props {
    addClasses?: string;
    children: string | JSX.Element | JSX.Element[];
    highlight?: 'overlay' | 'outline';
    acceptFileTypes: FileTypeKey[];
    handleDrop: HandleDrop;
    overlayClasses?: string;
    DropZoneContent(): JSX.Element;
}

/**
 * Allows a user to drag and drop one or more files and trigger a callback.
 * Note that all elements in contained in the dropzone will have their mouse events disabled unless
 * you give them the `respond-to-mouse` class.
 */
export const FileDropZone = ({
    addClasses,
    children,
    acceptFileTypes,
    handleDrop,
    highlight = 'overlay',
    overlayClasses,
    DropZoneContent,
}: Props): JSX.Element => {
    const [isDragActive, setIsDragActive] = useState(false);
    const [dragEnterTarget, setDragEnterTarget] = useState<
        (EventTarget & Element) | null
    >(null);
    const classNames = cx('file-drop-zone', 'relative', addClasses, {
        outline: highlight === 'outline',
        'drag-active': isDragActive,
    });

    return (
        <div
            className={classNames}
            onDragEnter={(e: React.DragEvent): void => {
                e.preventDefault();
                e.stopPropagation();

                if (dragEnterTarget === null) {
                    setDragEnterTarget(e.currentTarget);
                    setIsDragActive(true);
                }
            }}
            onDragOver={(e: React.DragEvent): void => {
                e.preventDefault();
            }}
            onDrop={(e: React.DragEvent): void => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragActive(false);
                setDragEnterTarget(null);

                if (e.dataTransfer.files) {
                    const validFiles = [];
                    const invalidFiles = [];

                    for (let i = 0; i < e.dataTransfer.files.length; i += 1) {
                        const file = e.dataTransfer.files[i];

                        if (hasAcceptedFileType(file, acceptFileTypes)) {
                            validFiles.push(file);
                        } else {
                            invalidFiles.push(file);
                        }
                    }

                    handleDrop({
                        validFiles,
                        invalidFiles,
                    });
                }
            }}
            onDragLeave={(e: React.DragEvent): void => {
                e.preventDefault();
                if (e.currentTarget === dragEnterTarget) {
                    setIsDragActive(false);
                    setDragEnterTarget(null);
                }
            }}
            data-cy="file-drop-zone"
        >
            <div className="top-0 left-0">{children}</div>
            {isDragActive && highlight === 'overlay' && (
                <div
                    className={cx(
                        'file-drop-zone-overlay absolute top-0 left-0 h-full w-full flex flex-col justify-center items-center',
                        overlayClasses,
                        addClasses,
                    )}
                >
                    <DropZoneContent />
                </div>
            )}
        </div>
    );
};
